import { useCallback, useRef } from "react";

/**
 * Detect a long press on touch devices and right-click on desktop.
 * Mouse long-press is intentionally disabled to avoid interfering with
 * text selection. Desktop users rely on the kebab menu / right-click.
 *
 * Usage:
 *   const longPress = useLongPress(() => openMenu(), { threshold: 500 });
 *   <div {...longPress}>...</div>
 */
export function useLongPress(onLongPress, { threshold = 500 } = {}) {
  const timerRef = useRef(null);

  const isExcluded = (e) =>
    !!e?.target?.closest?.(
      "button, a, input, textarea, select, [data-no-longpress]"
    );

  const start = useCallback(
    (e) => {
      if (isExcluded(e)) return;
      timerRef.current = setTimeout(() => onLongPress(e), threshold);
    },
    [onLongPress, threshold]
  );

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  return {
    onTouchStart: start,
    onTouchEnd: cancel,
    onTouchMove: cancel,
    onTouchCancel: cancel,
    onContextMenu: (e) => {
      if (isExcluded(e)) return;
      e.preventDefault();
      onLongPress(e);
    },
  };
}
